/**
 * `ButterPop` — the app's toast surface. It replaces `SuccessToast`, whose whole API was one
 * `message` prop and whose whole contract was *the caller owns timing, this owns rendering*.
 *
 * **The imperative half is what inverts that contract, and it is the point.** A caller says
 * `ButterPop.show({ message, duration })` from wherever the write landed — an event handler, a
 * `catch`, an effect — and holds no `toastMessage` state and no `setTimeout` of its own. What it
 * gets back is a `ToastId`, which is the only handle it needs: `ButterPop.remove(id)` takes the
 * notice down early, which is what an effect's cleanup wants and what `SuccessToast`'s caller
 * used to write by hand.
 *
 * **It is the ButterPop.js API, not the ButterPop.js package**, and the difference is not
 * pedantry. That library appends its container to `document.body` and pulls its stylesheet from a
 * CDN at runtime, and both are wrong in this shell. `document.body` is outside `.app-frame-inner`,
 * so a toast would sit outside `portraitLock`'s counter-rotation — D132 ignores rotation rather
 * than answering it, so a landscape phone's overlay is only upright because the shell rotated it —
 * and outside the `env(safe-area-inset-*)` contract that `:root` alone owns. And a CDN stylesheet
 * does not reach an offline device, which is most of what this PWA is for: the toast would fall
 * back to the library's own palette on exactly the launches where the app is least able to explain
 * itself. Rendering through `FrameOverlay` in this app's own tokens keeps both, and costs one file.
 *
 * **A host must be mounted for a toast to be seen, and the store does not care whether one is.**
 * `show()` records the toast and starts its clock whether or not a `ToastHost` is on screen —
 * which is the honest behaviour, not a gap: a duration is a promise about wall-clock time, so a
 * notice that spent its life on an unmounted screen has expired rather than queued. A message that
 * must survive a navigation is `pendingToast.ts`'s job, and always was.
 *
 * **Defaults deviate from the library's twice, deliberately.** `position` is `bottom-center` rather
 * than `top-right` — the top of this frame is the app bar, and the inline corners of a 375px phone
 * are not a place a one-line notice reads from — and `duration` is 4s rather than 5s, matching the
 * hold `GateDetailScreen` had already settled on for the same message.
 */
import { useSyncExternalStore } from 'react'

import { useTranslation } from '../i18n/I18nProvider'
import { Icon, type IconName } from './icons/Icon'

export type ToastType = 'success' | 'error' | 'warning' | 'info'

export type ToastPosition =
  'top-left' | 'top-center' | 'top-right' | 'center' | 'bottom-left' | 'bottom-center' | 'bottom-right'

/**
 * `default` is the tinted surface `SuccessToast` wore — the type's own wash behind the type's own
 * ink. `solid` fills with the type colour and writes in `--inverse`.
 *
 * **Neither is a light/dark choice**, which is what the library's `theme` means and what this one
 * must not: D188 made the scheme a device-local preference the whole app reads from
 * `[data-scheme]`, so a component that picked its own would be the one surface that ignored it.
 * Both treatments are built from per-scheme tokens and so carry no scheme rule of their own.
 */
export type ToastTheme = 'default' | 'solid'

export type ToastId = number

export type ToastOptions = {
  readonly message: string
  readonly type?: ToastType
  readonly position?: ToastPosition
  readonly theme?: ToastTheme
  /** Milliseconds on screen. **`0` is sticky** — no timer is armed, so only a tap takes it down. */
  readonly duration?: number
  readonly progress?: boolean
  readonly closable?: boolean
  readonly pauseOnHover?: boolean
  readonly closeOnClick?: boolean
  /** Fires once the notice is actually gone, however it went — timer, tap, or `remove()`. */
  readonly onClose?: () => void
}

type Toast = {
  readonly id: ToastId
  readonly message: string
  readonly type: ToastType
  readonly position: ToastPosition
  readonly theme: ToastTheme
  readonly duration: number
  readonly progress: boolean
  readonly closable: boolean
  readonly pauseOnHover: boolean
  readonly closeOnClick: boolean
  /** Playing out its exit. Still rendered, no longer counted down, and refuses a second dismiss. */
  readonly leaving: boolean
  readonly paused: boolean
  /** What is left of `duration` while `paused`. Meaningless otherwise, and not read otherwise. */
  readonly remaining: number
  readonly onClose?: () => void
}

const DEFAULT_DURATION_MS = 4_000

/**
 * How long the exit lasts before the toast is dropped from the store.
 *
 * **The removal is a timer, not an `animationend` listener**, and that is what keeps this
 * testable: the global `prefers-reduced-motion` reset in `global.css` squashes every animation to
 * 0.01ms, so a listener-driven removal would fire at a different moment for a resident who asked
 * for less motion than for one who did not — and jsdom fires no animation events at all, so a
 * suite could only ever prove the reduced case.
 */
const EXIT_MS = 180

const ICONS: Readonly<Record<ToastType, IconName>> = {
  success: 'check',
  error: 'octagon-x',
  // `warning` and `info` share a glyph because the icon set has no triangle, and the colour is
  // what separates them anyway (`--warning-text` on `--warning-surface` against `--info-*`).
  // A borrowed glyph that reads as "read this" is better than a borrowed one that reads as a
  // different verb — `circle-help` asks a question neither of these is asking.
  warning: 'info',
  info: 'info',
}

type TimerRecord = { readonly handle: number; readonly endsAt: number }

let toasts: readonly Toast[] = []
let nextId: ToastId = 1
const listeners = new Set<() => void>()
const timers = new Map<ToastId, TimerRecord>()

function publish(next: readonly Toast[]): void {
  toasts = next
  for (const listener of listeners) listener()
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener)
  return () => {
    listeners.delete(listener)
  }
}

/** `useSyncExternalStore`'s snapshot half — a stable reference until `publish` replaces it. */
function getToasts(): readonly Toast[] {
  return toasts
}

function clearTimer(id: ToastId): void {
  const record = timers.get(id)
  if (record === undefined) return
  window.clearTimeout(record.handle)
  timers.delete(id)
}

function arm(id: ToastId, ms: number): void {
  if (ms <= 0) return
  timers.set(id, { handle: window.setTimeout(() => dismiss(id), ms), endsAt: Date.now() + ms })
}

function patch(id: ToastId, fields: Partial<Toast>): void {
  publish(toasts.map((toast) => (toast.id === id ? { ...toast, ...fields } : toast)))
}

/**
 * Take a toast down **immediately**, skipping the exit.
 *
 * This is what `ButterPop.remove()` and `clearAll()` do, and the split from `dismiss()` is the
 * one design decision in this module worth stating. A caller that removes a toast has decided it
 * should not be on screen — usually an effect cleanup, which under `StrictMode`'s double-invoked
 * mount is racing its own second run — and an exit animation there means two toasts overlapping
 * for 180ms. A caller that *dismisses* one (the timer, the close button, a tap) is ending
 * something the resident was reading, and gets the animation.
 */
function drop(id: ToastId): void {
  const toast = toasts.find((candidate) => candidate.id === id)
  if (toast === undefined) return
  clearTimer(id)
  publish(toasts.filter((candidate) => candidate.id !== id))
  toast.onClose?.()
}

function dismiss(id: ToastId): void {
  const toast = toasts.find((candidate) => candidate.id === id)
  if (toast === undefined || toast.leaving) return
  clearTimer(id)
  patch(id, { leaving: true, paused: false })
  timers.set(id, { handle: window.setTimeout(() => drop(id), EXIT_MS), endsAt: Date.now() + EXIT_MS })
}

function pause(id: ToastId): void {
  const record = timers.get(id)
  const toast = toasts.find((candidate) => candidate.id === id)
  if (record === undefined || toast === undefined || toast.leaving || toast.paused) return
  clearTimer(id)
  patch(id, { paused: true, remaining: Math.max(0, record.endsAt - Date.now()) })
}

function resume(id: ToastId): void {
  const toast = toasts.find((candidate) => candidate.id === id)
  if (toast === undefined || !toast.paused) return
  patch(id, { paused: false })
  arm(id, toast.remaining)
}

function show(options: ToastOptions): ToastId {
  const id = nextId++
  const duration = options.duration ?? DEFAULT_DURATION_MS
  const toast: Toast = {
    id,
    message: options.message,
    type: options.type ?? 'success',
    position: options.position ?? 'bottom-center',
    theme: options.theme ?? 'default',
    duration,
    progress: options.progress ?? false,
    closable: options.closable ?? false,
    pauseOnHover: options.pauseOnHover ?? true,
    closeOnClick: options.closeOnClick ?? false,
    leaving: false,
    paused: false,
    remaining: duration,
    ...(options.onClose === undefined ? {} : { onClose: options.onClose }),
  }
  publish([...toasts, toast])
  arm(id, duration)
  return id
}

function shorthand(type: ToastType) {
  return (message: string, options: Omit<ToastOptions, 'message' | 'type'> = {}): ToastId =>
    show({ ...options, message, type })
}

function clearAll(): void {
  for (const toast of [...toasts]) drop(toast.id)
}

export const ButterPop = {
  show,
  success: shorthand('success'),
  error: shorthand('error'),
  warning: shorthand('warning'),
  info: shorthand('info'),
  /** Immediate — see `drop()`. Unknown ids are a no-op, so a stale cleanup is safe. */
  remove: drop,
  clearAll,
}

function ToastCard({
  toast,
  closeLabel,
}: {
  readonly toast: Toast
  readonly closeLabel: string
}): JSX.Element {
  const className = [
    'butterpop-toast',
    `butterpop-${toast.type}`,
    `butterpop-theme-${toast.theme}`,
    toast.leaving ? 'butterpop-leaving' : '',
  ]
    .filter((part) => part !== '')
    .join(' ')

  return (
    <div
      className={className}
      // An error interrupts because it says something the resident has to act on; the other three
      // are ambient and wait their turn in the queue the screen reader is already reading.
      role={toast.type === 'error' ? 'alert' : 'status'}
      aria-live={toast.type === 'error' ? 'assertive' : 'polite'}
      {...(toast.closeOnClick ? { onClick: () => dismiss(toast.id) } : {})}
      {...(toast.pauseOnHover
        ? { onPointerEnter: () => pause(toast.id), onPointerLeave: () => resume(toast.id) }
        : {})}
    >
      <Icon name={ICONS[toast.type]} size={16} />
      {/* `unicode-bidi: plaintext` lives on this span rather than the card: a toast routinely
          carries a gate name, and an LTR name in this RTL shell reads from the wrong end
          otherwise. The lesson is in AGENTS.md §4.7 — Hebrew-only seed data hides the whole
          class. */}
      <span className="butterpop-message">{toast.message}</span>
      {toast.closable ? (
        <button
          type="button"
          className="butterpop-close"
          aria-label={closeLabel}
          onClick={(event) => {
            // Without this, a `closeOnClick` toast counts one tap twice — harmless today, since
            // both paths call `dismiss`, and exactly the kind of coincidence that stops being one
            // when a close button grows a confirm.
            event.stopPropagation()
            dismiss(toast.id)
          }}
        >
          <Icon name="x" size={16} />
        </button>
      ) : null}
      {toast.progress && toast.duration > 0 ? (
        <span
          className="butterpop-progress"
          aria-hidden="true"
          style={{
            animationDuration: `${toast.duration}ms`,
            animationPlayState: toast.paused ? 'paused' : 'running',
          }}
        />
      ) : null}
    </div>
  )
}

/**
 * Where the toasts render. Mount it once per screen that shows them, inside a `FrameOverlay` —
 * the same anchoring `SuccessToast` needed and for the same reason: `.app-screen` is the scroll
 * container, so an absolute offset resolved against it moves with the scroll.
 *
 * It renders one container per occupied position rather than seven empty ones, and nothing at all
 * when the store is empty, so an idle screen carries no extra boxes.
 */
export function ToastHost(): JSX.Element | null {
  const items = useSyncExternalStore(subscribe, getToasts, getToasts)
  const { t } = useTranslation('common')

  if (items.length === 0) return null
  const positions = [...new Set(items.map((toast) => toast.position))]

  return (
    <>
      {positions.map((position) => (
        <div key={position} className={`butterpop-container butterpop-at-${position}`}>
          {items
            .filter((toast) => toast.position === position)
            .map((toast) => (
              <ToastCard key={toast.id} toast={toast} closeLabel={t('close')} />
            ))}
        </div>
      ))}
    </>
  )
}
